/**
 * Fleet power availability — shared contract
 *
 * Point-in-time breakdown of stored/derived power state across machines seen
 * within a selectable reachability window. Consumed by realtime-api aggregation
 * and the admin-dashboard Host Mode Participation page.
 */

import { z } from 'zod';

// ============================================================================
// Segments
// ============================================================================

export const powerAvailabilitySegmentValues = [
  'awake',
  'asleep-s0',
  'asleep-s3',
  'hibernate-s4',
  'asleep-other',
  'off',
  'unknown',
  'no-data',
] as const;

export type PowerAvailabilitySegment = (typeof powerAvailabilitySegmentValues)[number];

export const powerAvailabilitySegmentSchema = z.enum(powerAvailabilitySegmentValues);

export const POWER_AVAILABILITY_SEGMENT_LABELS: Record<PowerAvailabilitySegment, string> = {
  awake: 'Awake',
  'asleep-s0': 'Asleep (S0)',
  'asleep-s3': 'Asleep (S3)',
  'hibernate-s4': 'Hibernated (S4)',
  'asleep-other': 'Asleep',
  off: 'Off',
  unknown: 'Unknown',
  'no-data': 'No power data',
};

/** Display order for the stacked bar (matches fleet Power column semantics). */
export const POWER_AVAILABILITY_SEGMENT_DISPLAY_ORDER: PowerAvailabilitySegment[] = [
  'awake',
  'asleep-s0',
  'asleep-s3',
  'hibernate-s4',
  'asleep-other',
  'off',
  'unknown',
  'no-data',
];

// ============================================================================
// Reachability window
// ============================================================================

/** Allowed window options (days). 1 = last 24 hours. */
export const POWER_AVAILABILITY_WINDOW_OPTIONS = [1, 7, 30] as const;

export type PowerAvailabilityWindowDays = (typeof POWER_AVAILABILITY_WINDOW_OPTIONS)[number];

export const powerAvailabilityWindowDaysSchema = z.union(
  POWER_AVAILABILITY_WINDOW_OPTIONS.map((days) => z.literal(days)) as [
    z.ZodLiteral<(typeof POWER_AVAILABILITY_WINDOW_OPTIONS)[0]>,
    z.ZodLiteral<(typeof POWER_AVAILABILITY_WINDOW_OPTIONS)[1]>,
    z.ZodLiteral<(typeof POWER_AVAILABILITY_WINDOW_OPTIONS)[2]>,
  ],
);

export const DEFAULT_POWER_AVAILABILITY_WINDOW_DAYS: PowerAvailabilityWindowDays = 7;

export function clampPowerAvailabilityWindowDays(input: number | undefined): PowerAvailabilityWindowDays {
  if (typeof input !== 'number' || !Number.isFinite(input)) {
    return DEFAULT_POWER_AVAILABILITY_WINDOW_DAYS;
  }
  const floored = Math.floor(input);
  if ((POWER_AVAILABILITY_WINDOW_OPTIONS as readonly number[]).includes(floored)) {
    return floored as PowerAvailabilityWindowDays;
  }
  return DEFAULT_POWER_AVAILABILITY_WINDOW_DAYS;
}

export function parsePowerAvailabilityWindowDaysParam(raw: unknown): PowerAvailabilityWindowDays | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (typeof raw !== 'string') {
    throw new Error('windowDays must be a positive integer (1, 7, or 30)');
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    throw new Error('windowDays must be a positive integer (1, 7, or 30)');
  }
  if (!(POWER_AVAILABILITY_WINDOW_OPTIONS as readonly number[]).includes(parsed)) {
    throw new Error('windowDays must be 1, 7, or 30');
  }
  return parsed as PowerAvailabilityWindowDays;
}

// ============================================================================
// API response
// ============================================================================

export const powerAvailabilitySegmentRowSchema = z.object({
  segment: powerAvailabilitySegmentSchema,
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export type PowerAvailabilitySegmentRow = z.infer<typeof powerAvailabilitySegmentRowSchema>;

export const hostNonsolePowerAvailabilityResponseSchema = z.object({
  generatedAt: z.number(),
  windowDays: powerAvailabilityWindowDaysSchema,
  population: z.number().int().nonnegative(),
  reportingCount: z.number().int().nonnegative(),
  /** Fraction of population with any power state (1 - no-data/population). */
  coverage: z.number().min(0).max(1),
  segments: z.array(powerAvailabilitySegmentRowSchema),
});

export type HostNonsolePowerAvailabilityResponse = z.infer<
  typeof hostNonsolePowerAvailabilityResponseSchema
>;
